import React, { ReactNode } from "react";
import { AsyncTypeahead } from "react-bootstrap-typeahead";

import { searchFilterValues } from "../../utils/Requests";

type SearchableFilterDropdownProperties = {
    filter: DropdownFilter,
    customerConfig: CustomerConfig,
    callback: (qsParam: Record<string, string>) => void,
    value?: string
}

type SearchableFilterDropdownState = {
    loading: boolean,
    options: Array<{ id: string; label: string }>,
    selectedValues: Array<{ value: string; label: string }>
}

export default class SearchableFilterDropdown extends React.Component<
    SearchableFilterDropdownProperties,
    SearchableFilterDropdownState
> {
    constructor(props: SearchableFilterDropdownProperties) {
        super(props);

        // Initialize selected values from incoming value prop
        const initialSelected = this.parseInitialValues(props.value);

        this.state = {
            loading: false,
            options: [],
            selectedValues: initialSelected
        };
    }

    /**
     * Parse comma-separated value string into selected value objects.
     * When editing an existing model, we only have IDs, so we use them as labels too.
     */
    private parseInitialValues(value?: string): Array<{ value: string; label: string }> {
        if (!value || !value.trim()) return [];
        return value.split(",").map(v => {
            const trimmed = v.trim();
            return { value: trimmed, label: trimmed };
        });
    }

    public componentDidUpdate(prevProps: SearchableFilterDropdownProperties): void {
        // Handle external value changes (e.g., when loading a saved model)
        if (prevProps.value !== this.props.value) {
            const newSelected = this.parseInitialValues(this.props.value);
            this.setState({ selectedValues: newSelected });
        }
    }

    public reset(): void {
        this.setState({
            selectedValues: [],
            options: []
        });
    }

    private handleSearch = async (query: string): Promise<void> => {
        this.setState({ loading: true });

        const { filter, customerConfig } = this.props;
        // Use fieldKey if available, otherwise fall back to qsParam
        const fieldName = filter.fieldKey || filter.qsParam;

        try {
            const results = await searchFilterValues(customerConfig, fieldName, query, 10);
            // Map API response to options format
            const options = results.map(r => ({ id: r.id, label: r.label }));
            this.setState({ options, loading: false });
        } catch {
            // Graceful degradation on error - show empty options
            this.setState({ options: [], loading: false });
        }
    }

    private handleChange = (selected: Array<any>): void => {
        // Convert to our internal format
        const selectedValues = selected.map(item => ({
            value: item.id || item.value || item,
            label: item.label || item.id || item.value || item
        }));

        this.setState({ selectedValues });

        const { filter, callback } = this.props;
        if (filter.qsParam) {
            const qsObj: Record<string, string> = {};
            if (selectedValues.length > 0) {
                // Send comma-separated IDs like other filters
                const values = selectedValues.map(item => item.value);
                qsObj[filter.qsParam] = values.join(",");
            } else {
                qsObj[filter.qsParam] = "";
            }
            callback(qsObj);
        }
    }

    public render(): ReactNode {
        const { filter } = this.props;
        const isMultiple = filter.cardinality === "multiple";

        // Map selected values to the format AsyncTypeahead expects
        const selected = this.state.selectedValues.map(v => ({
            id: v.value,
            label: v.label
        }));

        return (
            <AsyncTypeahead
                id={`searchable-${filter.id}`}
                labelKey="label"
                multiple={isMultiple}
                filterBy={() => true}  // Server-side filtering, bypass client-side
                isLoading={this.state.loading}
                onSearch={this.handleSearch}
                options={this.state.options}
                onChange={this.handleChange}
                selected={selected}
                minLength={1}
                placeholder={`Search ${filter.name}...`}
                clearButton
                emptyLabel="No results found"
            />
        );
    }
}

